import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Snackbar from '@material-ui/core/Snackbar'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import StarsIcon from '@material-ui/icons/Stars'

import {
  generateWipReportAiOration,
  generateWipReportSummary,
} from '../../../entities/wipReportSummary/service'
import WipSummaryFollowUpDialog from './WipSummaryFollowUpDialog'
import WipSystemPromptManager from './WipSystemPromptManager'

const useStyles = makeStyles(theme => ({
  actionsGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  generateBtn: {
    flexShrink: 0,
  },
  promptHint: {
    width: '100%',
    marginBottom: theme.spacing(1),
  },
}))

const WipAiSummaryActions = props => {
  const classes = useStyles()
  const {
    client,
    projectId,
    month,
    value,
    onChange,
    onInternalChange,
    onInternalLoadingChange,
    onGenerated,
  } = props

  const conversationRef = useRef([])

  const [dialogOpen, setDialogOpen] = useState(false)
  const [matterContext, setMatterContext] = useState('')
  const [loading, setLoading] = useState(false)
  const [refineLoading, setRefineLoading] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })

  const [selectedPrompt, setSelectedPrompt] = useState({ id: 'default', name: 'default' })
  const [promptManagerOpen, setPromptManagerOpen] = useState(false)
  const [refineOpen, setRefineOpen] = useState(false)
  const [hasAiHistory, setHasAiHistory] = useState(false)

  useEffect(() => {
    conversationRef.current = []
    setHasAiHistory(false)
  }, [projectId, month, selectedPrompt.id])

  const canGenerate = Boolean(client && projectId && month)

  const handleOpenDialog = () => {
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    if (!loading) setDialogOpen(false)
  }

  const generateInternalSummary = async idempotencyKey => {
    if (!onInternalChange) return
    if (onInternalLoadingChange) onInternalLoadingChange(true)
    try {
      const internalText = await generateWipReportAiOration(
        client,
        String(projectId),
        String(month),
        { idempotencyKey },
      )
      onInternalChange(internalText || '')
    } catch (e) {
      console.error(e)
      setSnackbar({
        open: true,
        message: e?.message || 'Could not generate internal summary.',
      })
    } finally {
      if (onInternalLoadingChange) onInternalLoadingChange(false)
    }
  }

  const handleGenerate = async () => {
    if (!canGenerate) return
    conversationRef.current = []
    setHasAiHistory(false)
    setLoading(true)
    const idempotencyKey =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()
            .toString(36)
            .slice(2)}`
    try {
      const text = await generateWipReportSummary(
        client,
        String(projectId),
        String(month),
        matterContext.trim() || undefined,
        {
          systemPromptId: selectedPrompt.id,
          conversation: null,
          idempotencyKey,
        },
      )
      onChange(text || '')
      conversationRef.current = [{ role: 'assistant', content: text || '' }]
      setHasAiHistory(true)
      setDialogOpen(false)
      setSnackbar({ open: true, message: 'Summary generated' })
      await generateInternalSummary(idempotencyKey)
      if (onGenerated) onGenerated()
    } catch (e) {
      console.error(e)
      setSnackbar({
        open: true,
        message: e?.message || 'Could not generate summary. Try again.',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRefineSubmit = async instruction => {
    if (!canGenerate) return
    setRefineLoading(true)
    try {
      const payload = [...conversationRef.current, { role: 'user', content: instruction }]
      const text = await generateWipReportSummary(
        client,
        String(projectId),
        String(month),
        matterContext.trim() || undefined,
        {
          systemPromptId: selectedPrompt.id,
          conversation: payload,
        },
      )
      onChange(text || '')
      conversationRef.current = [...payload, { role: 'assistant', content: text || '' }]
      setHasAiHistory(true)
      setSnackbar({ open: true, message: 'Summary refined' })
    } catch (e) {
      console.error(e)
      setSnackbar({
        open: true,
        message: e?.message || 'Could not refine summary. Try again.',
      })
    } finally {
      setRefineLoading(false)
    }
  }

  return (
    <>
      <div className={classes.actionsGroup}>
        <Button
          type="button"
          variant="outlined"
          size="small"
          disabled={!canGenerate || loading}
          onClick={() => setPromptManagerOpen(true)}
        >
          LLM Instructions (System Prompt)
        </Button>
        <Button
          type="button"
          variant="outlined"
          color="primary"
          size="small"
          className={classes.generateBtn}
          disabled={!canGenerate || loading}
          startIcon={
            loading ? <CircularProgress size={16} /> : <StarsIcon style={{ fontSize: 18 }} />
          }
          onClick={handleOpenDialog}
        >
          Generate with AI
        </Button>
      </div>
      <Typography
        className={classes.promptHint}
        variant="caption"
        color="textSecondary"
        component="p"
      >
        Using system prompt: <strong>{selectedPrompt.name}</strong>
      </Typography>

      <WipSystemPromptManager
        open={promptManagerOpen}
        onClose={() => setPromptManagerOpen(false)}
        client={client}
        selectedPrompt={selectedPrompt}
        onSelectPrompt={setSelectedPrompt}
      />

      <WipSummaryFollowUpDialog
        open={refineOpen}
        onClose={() => !refineLoading && setRefineOpen(false)}
        currentSummary={value}
        onSubmit={handleRefineSubmit}
        loading={refineLoading}
      />

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Matter context</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Matter context"
            placeholder="Add context for this billing period (optional but recommended)…"
            fullWidth
            multiline
            minRows={4}
            variant="outlined"
            value={matterContext}
            onChange={e => setMatterContext(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleGenerate} color="primary" variant="contained" disabled={loading}>
            {loading ? 'Generating…' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </>
  )
}

WipAiSummaryActions.propTypes = {
  client: PropTypes.object,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  month: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onInternalChange: PropTypes.func,
  onInternalLoadingChange: PropTypes.func,
  onGenerated: PropTypes.func,
}

WipAiSummaryActions.defaultProps = {
  client: null,
  projectId: null,
  month: null,
  value: '',
  onInternalChange: null,
  onInternalLoadingChange: null,
  onGenerated: null,
}

export default WipAiSummaryActions
